/**
 * Session spots-left alert.
 *
 * When active bookings leave exactly `threshold` places (default 3), tell
 * eligible swimmers who are not already in. One message per family. A session
 * is marked sent the first time, so 3 → 4 → 3 does not send again.
 */

import { resolveAppOriginForUser, resolveAppUrlForUser } from './appOrigin';
import { canCheckInToSessionByName } from './crossSquadCheckIn';
import { formatSessionWhenLong } from './kitReminders';
import { sharedSessionSquadLabel } from './sharedSlot';
import { timeToMinutes, type NzWallClock } from './time';

export const SPOTS_LEFT_NOTICE_KIND = 'session_spots_left';

export const DEFAULT_SPOTS_LEFT_THRESHOLD = 3;
export const DEFAULT_SPOTS_LEFT_LEAD_MINUTES = 60;

export type SpotsLeftAlertSettings = {
  enabled: boolean;
  threshold: number;
  min_lead_minutes: number;
};

/** On when a club has not saved a value. WASC is also set explicitly in migration 0093. */
export const DEFAULT_SPOTS_LEFT_ALERT_SETTINGS: SpotsLeftAlertSettings = {
  enabled: true,
  threshold: DEFAULT_SPOTS_LEFT_THRESHOLD,
  min_lead_minutes: DEFAULT_SPOTS_LEFT_LEAD_MINUTES,
};

const OCCUPYING_OR_WAITLIST = new Set(['booked', 'checked_in', 'waitlist']);

export type SpotsLeftGuardian = {
  userId: string;
  email: string | null;
  isPrimary: boolean;
  receivesComms: boolean;
  archived: boolean;
};

export type SpotsLeftSwimmerInput = {
  swimmerId: string;
  userId: string;
  name: string;
  squadName: string;
  status: string;
  archived: boolean;
  managesOwnAccount: boolean;
  juniorCoach: boolean;
  checkInStatus: string | null;
  cancelReason: string | null;
  email: string | null;
  guardians: SpotsLeftGuardian[];
};

export type SpotsLeftRecipient = {
  userId: string;
  email: string | null;
  /** False when the address is missing or a placeholder, or comms are off. */
  emailAllowed: boolean;
  self: boolean;
};

export type SpotsLeftFamilyMessage = {
  swimmerIds: string[];
  swimmerNames: string[];
  recipients: SpotsLeftRecipient[];
};

export type SpotsLeftDecisionReason =
  | 'send'
  | 'disabled'
  | 'already_sent'
  | 'not_occupying'
  | 'not_crossing'
  | 'not_scheduled'
  | 'started_or_past'
  | 'inside_lead_window'
  | 'bad_threshold';

export type SpotsLeftDecision = {
  send: boolean;
  spotsLeft: number;
  reason: SpotsLeftDecisionReason;
};

export type SpotsLeftMarkerState = {
  sent: boolean;
  occupying: number;
};

export function spotsLeftPhrase(threshold: number): string {
  return threshold === 1 ? '1 spot left' : `${threshold} spots left`;
}

export function normalizeSpotsLeftAlertSettings(raw: unknown): SpotsLeftAlertSettings {
  const obj = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const threshold = Number(obj.threshold);
  const lead = Number(obj.min_lead_minutes);
  return {
    enabled: obj.enabled === undefined ? DEFAULT_SPOTS_LEFT_ALERT_SETTINGS.enabled : obj.enabled === true,
    threshold:
      Number.isInteger(threshold) && threshold >= 1 && threshold <= 50
        ? threshold
        : DEFAULT_SPOTS_LEFT_THRESHOLD,
    min_lead_minutes:
      Number.isInteger(lead) && lead >= 0 && lead <= 24 * 60 ? lead : DEFAULT_SPOTS_LEFT_LEAD_MINUTES,
  };
}

/** Minutes from `now` (NZ wall clock) until the session starts. Negative once it has started or the date has passed. */
export function minutesUntilSessionStart(
  sessionDate: string,
  startTime: string,
  now: NzWallClock,
): number {
  const start = timeToMinutes(startTime);
  if (!Number.isFinite(start)) return -1;
  const [y1, m1, d1] = now.isoDate.split('-').map((part) => Number.parseInt(part, 10));
  const [y2, m2, d2] = sessionDate.split('-').map((part) => Number.parseInt(part, 10));
  if (![y1, m1, d1, y2, m2, d2].every((n) => Number.isFinite(n))) return -1;
  const days = Math.round(
    (Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000,
  );
  return days * 24 * 60 + start - now.totalMinutes;
}

export function isDeliverableEmail(email: string | null | undefined): boolean {
  const value = email?.trim().toLowerCase() ?? '';
  if (!value.includes('@') || value.startsWith('@')) return false;
  if (value.endsWith('.invalid')) return false;
  if (value.includes('local.marshal.invalid')) return false;
  return true;
}

/**
 * Active swimmers whose squad can book this session, who are not already in
 * and have not declined it (can't come, including away and not my usual session).
 */
export function isSpotsLeftEligibleSwimmer(
  swimmer: SpotsLeftSwimmerInput,
  sessionSquadName: string,
): boolean {
  if (swimmer.archived) return false;
  if (swimmer.status !== 'active') return false;
  if (!swimmer.squadName || !canCheckInToSessionByName(swimmer.squadName, sessionSquadName)) return false;
  if (swimmer.checkInStatus && OCCUPYING_OR_WAITLIST.has(swimmer.checkInStatus)) return false;
  if (swimmer.checkInStatus === 'cancelled') return false;
  return true;
}

function managersFor(swimmer: SpotsLeftSwimmerInput): SpotsLeftRecipient[] {
  if (swimmer.managesOwnAccount || swimmer.juniorCoach) {
    return [
      {
        userId: swimmer.userId,
        email: swimmer.email,
        emailAllowed: isDeliverableEmail(swimmer.email),
        self: true,
      },
    ];
  }
  const active = swimmer.guardians.filter((g) => g.receivesComms && !g.archived && g.userId);
  const primaries = active.filter((g) => g.isPrimary);
  const chosen = primaries.length > 0 ? primaries : active;
  const seen = new Set<string>();
  const out: SpotsLeftRecipient[] = [];
  for (const guardian of chosen) {
    if (seen.has(guardian.userId)) continue;
    seen.add(guardian.userId);
    out.push({
      userId: guardian.userId,
      email: guardian.email,
      emailAllowed: isDeliverableEmail(guardian.email),
      self: false,
    });
  }
  return out;
}

function findRoot(parent: Map<string, string>, id: string): string {
  let cursor = id;
  while (parent.get(cursor) && parent.get(cursor) !== cursor) {
    cursor = parent.get(cursor)!;
  }
  parent.set(id, cursor);
  return cursor;
}

/**
 * One message per family. Siblings who share a managing guardian are listed
 * together. Swimmers who manage their own login, and junior coaches, are
 * told themselves and are not folded into the parent message.
 */
export function selectSpotsLeftFamilies(
  swimmers: readonly SpotsLeftSwimmerInput[],
  sessionSquadName: string,
): SpotsLeftFamilyMessage[] {
  const eligible = swimmers.filter((s) => isSpotsLeftEligibleSwimmer(s, sessionSquadName));
  const selfManaged = eligible.filter((s) => s.managesOwnAccount || s.juniorCoach);
  const managed = eligible.filter((s) => !s.managesOwnAccount && !s.juniorCoach);

  const families: SpotsLeftFamilyMessage[] = [];

  for (const swimmer of selfManaged) {
    const recipients = managersFor(swimmer);
    if (recipients.length === 0) continue;
    families.push({
      swimmerIds: [swimmer.swimmerId],
      swimmerNames: [swimmer.name],
      recipients,
    });
  }

  const parent = new Map<string, string>();
  const byId = new Map(managed.map((s) => [s.swimmerId, s]));
  for (const swimmer of managed) parent.set(swimmer.swimmerId, swimmer.swimmerId);
  const byManager = new Map<string, string[]>();
  for (const swimmer of managed) {
    for (const recipient of managersFor(swimmer)) {
      const list = byManager.get(recipient.userId) ?? [];
      list.push(swimmer.swimmerId);
      byManager.set(recipient.userId, list);
    }
  }
  for (const ids of byManager.values()) {
    const root = ids[0];
    if (!root) continue;
    for (const id of ids.slice(1)) {
      parent.set(findRoot(parent, id), findRoot(parent, root));
    }
  }

  const groups = new Map<string, SpotsLeftSwimmerInput[]>();
  for (const swimmer of managed) {
    if (managersFor(swimmer).length === 0) continue;
    const root = findRoot(parent, swimmer.swimmerId);
    const list = groups.get(root) ?? [];
    list.push(swimmer);
    groups.set(root, list);
  }

  for (const group of groups.values()) {
    const ordered = group.slice().sort((a, b) => a.name.localeCompare(b.name));
    const recipients: SpotsLeftRecipient[] = [];
    const seen = new Set<string>();
    for (const swimmer of ordered) {
      for (const recipient of managersFor(byId.get(swimmer.swimmerId) ?? swimmer)) {
        if (seen.has(recipient.userId)) continue;
        seen.add(recipient.userId);
        recipients.push(recipient);
      }
    }
    if (recipients.length === 0) continue;
    families.push({
      swimmerIds: ordered.map((s) => s.swimmerId),
      swimmerNames: ordered.map((s) => s.name),
      recipients,
    });
  }

  families.sort((a, b) => (a.swimmerNames[0] ?? '').localeCompare(b.swimmerNames[0] ?? ''));
  return families;
}

export function decideSpotsLeftAlert(input: {
  enabled: boolean;
  threshold: number;
  minLeadMinutes: number;
  capacity: number;
  occupyingBefore: number;
  occupyingAfter: number;
  alreadySent: boolean;
  sessionStatus: string;
  minutesUntilStart: number;
  placementOccupies: boolean;
}): SpotsLeftDecision {
  const spotsLeft = input.capacity - input.occupyingAfter;
  if (!input.enabled) return { send: false, spotsLeft, reason: 'disabled' };
  if (input.alreadySent) return { send: false, spotsLeft, reason: 'already_sent' };
  if (!Number.isInteger(input.threshold) || input.threshold < 1) {
    return { send: false, spotsLeft, reason: 'bad_threshold' };
  }
  if (!input.placementOccupies) return { send: false, spotsLeft, reason: 'not_occupying' };
  if (input.sessionStatus !== 'scheduled') return { send: false, spotsLeft, reason: 'not_scheduled' };
  if (input.minutesUntilStart <= 0) return { send: false, spotsLeft, reason: 'started_or_past' };
  if (input.minutesUntilStart < input.minLeadMinutes) {
    return { send: false, spotsLeft, reason: 'inside_lead_window' };
  }
  const before = input.capacity - input.occupyingBefore;
  const crossedDown = spotsLeft === input.threshold && before > input.threshold;
  if (!crossedDown) return { send: false, spotsLeft, reason: 'not_crossing' };
  return { send: true, spotsLeft, reason: 'send' };
}

/**
 * Apply one occupancy change. `sent` sticks once a crossing is accepted,
 * so a later return to the same number does not notify.
 */
export function stepSpotsLeftAlert(
  state: SpotsLeftMarkerState,
  occupyingAfter: number,
  ctx: {
    enabled: boolean;
    threshold: number;
    minLeadMinutes: number;
    capacity: number;
    sessionStatus: string;
    minutesUntilStart: number;
  },
): SpotsLeftMarkerState & { notify: boolean; reason: SpotsLeftDecisionReason; spotsLeft: number } {
  const decision = decideSpotsLeftAlert({
    ...ctx,
    occupyingBefore: state.occupying,
    occupyingAfter,
    alreadySent: state.sent,
    placementOccupies: occupyingAfter > state.occupying,
  });
  return {
    occupying: occupyingAfter,
    sent: state.sent || decision.send,
    notify: decision.send,
    reason: decision.reason,
    spotsLeft: decision.spotsLeft,
  };
}

export function joinNames(names: readonly string[]): string {
  const clean = names.map((name) => name.trim()).filter(Boolean);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0]!;
  if (clean.length === 2) return `${clean[0]} and ${clean[1]}`;
  return `${clean.slice(0, -1).join(', ')}, and ${clean[clean.length - 1]}`;
}

export type SpotsLeftCopyInput = {
  clubName: string;
  squadName: string;
  sessionDate: string;
  startTime: string;
  poolName: string;
  threshold: number;
  swimmerNames: readonly string[];
  bookUrl: string;
};

export function spotsLeftSquadLabel(squadName: string | null | undefined): string {
  return sharedSessionSquadLabel(squadName);
}

export function buildSpotsLeftNotice(input: SpotsLeftCopyInput): { title: string; body: string } {
  const phrase = spotsLeftPhrase(input.threshold);
  const squad = spotsLeftSquadLabel(input.squadName);
  const when = formatSessionWhenLong(input.sessionDate, input.startTime);
  const pool = input.poolName.trim() || 'the pool';
  const kids = joinNames(input.swimmerNames);
  const who = kids ? `${kids} can still book.` : 'There is still room to book.';
  return {
    title: phrase,
    body: `${input.clubName} · ${squad} · ${when} · ${pool}. ${phrase}. ${who}`,
  };
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildSpotsLeftEmail(input: SpotsLeftCopyInput): { subject: string; text: string; html: string } {
  const phrase = spotsLeftPhrase(input.threshold);
  const squad = spotsLeftSquadLabel(input.squadName);
  const when = formatSessionWhenLong(input.sessionDate, input.startTime);
  const pool = input.poolName.trim() || 'the pool';
  const kids = joinNames(input.swimmerNames);
  const who = kids ? `${kids} can still book.` : 'There is still room to book.';
  const subject = `${phrase} — ${squad} · ${when}`;
  const text = [
    'Kia ora,',
    '',
    `${input.clubName} · ${squad}`,
    when,
    pool,
    phrase,
    '',
    who,
    '',
    'Book in the parent app:',
    input.bookUrl,
    '',
    'Ngā mihi',
    input.clubName,
  ].join('\n');
  const html = [
    '<p>Kia ora,</p>',
    `<p><strong>${escapeHtml(input.clubName)}</strong><br />${escapeHtml(squad)}<br />${escapeHtml(when)}<br />${escapeHtml(pool)}</p>`,
    `<p><strong>${escapeHtml(phrase)}</strong></p>`,
    `<p>${escapeHtml(who)}</p>`,
    `<p><a href="${escapeHtml(input.bookUrl)}">Book in the parent app</a></p>`,
    `<p>Ngā mihi<br />${escapeHtml(input.clubName)}</p>`,
  ].join('\n');
  return { subject, text, html };
}

/** Absolute parent-app URL. Parents are kept off the committee site; a requested parent origin stays. */
export function spotsLeftBookUrl(input: {
  sessionId: string;
  roles?: readonly string[] | null;
  isPlatformOwner?: boolean;
  environment?: string | null;
  brandDomain?: string | null;
}): string {
  const requested = resolveAppOriginForUser({
    roles: ['parent'],
    environment: input.environment,
    brandDomain: input.brandDomain,
  });
  return resolveAppUrlForUser(`/sessions/${input.sessionId}`, {
    roles: input.roles ?? ['parent'],
    isPlatformOwner: input.isPlatformOwner,
    requestedOrigin: requested,
    environment: input.environment,
    brandDomain: input.brandDomain,
  });
}

export type SpotsLeftNoticeMeta = {
  swimmer_ids: string[];
  swimmer_names: string[];
  threshold: number;
  club_name: string;
  squad_label: string;
  when_label: string;
  pool_name: string;
};

/** In-app card stays until the session is full, started, cancelled, or every listed swimmer has booked. */
export function spotsLeftNoticeVisible(input: {
  sessionStatus: string;
  minutesUntilStart: number;
  occupying: number;
  capacity: number;
  swimmerIds: readonly string[];
  checkIns: readonly { swimmerId: string; status: string }[];
}): boolean {
  if (input.sessionStatus !== 'scheduled') return false;
  if (input.minutesUntilStart <= 0) return false;
  if (input.capacity > 0 && input.occupying >= input.capacity) return false;
  if (input.swimmerIds.length === 0) return false;
  const bySwimmer = new Map(input.checkIns.map((row) => [row.swimmerId, row.status]));
  return input.swimmerIds.some((id) => {
    const status = bySwimmer.get(id);
    return !status || !OCCUPYING_OR_WAITLIST.has(status);
  });
}

export function remainingSpotsLeftNames(
  meta: Pick<SpotsLeftNoticeMeta, 'swimmer_ids' | 'swimmer_names'>,
  checkIns: readonly { swimmerId: string; status: string }[],
): string[] {
  const bySwimmer = new Map(checkIns.map((row) => [row.swimmerId, row.status]));
  const names: string[] = [];
  meta.swimmer_ids.forEach((id, index) => {
    const status = bySwimmer.get(id);
    if (status && OCCUPYING_OR_WAITLIST.has(status)) return;
    const name = meta.swimmer_names[index]?.trim();
    if (name) names.push(name);
  });
  return names;
}

export function refreshSpotsLeftNoticeBody(
  meta: SpotsLeftNoticeMeta,
  checkIns: readonly { swimmerId: string; status: string }[],
): string {
  const names = remainingSpotsLeftNames(meta, checkIns);
  const phrase = spotsLeftPhrase(meta.threshold);
  const who = names.length > 0 ? `${joinNames(names)} can still book.` : 'There is still room to book.';
  return `${meta.club_name} · ${meta.squad_label} · ${meta.when_label} · ${meta.pool_name}. ${phrase}. ${who}`;
}

/** Push goes only to accounts that already have a subscription. Email goes to every allowed address. */
export function splitSpotsLeftDelivery(
  recipients: readonly SpotsLeftRecipient[],
  subscribedUserIds: ReadonlySet<string>,
): { pushUserIds: string[]; emailTo: string[] } {
  const pushUserIds: string[] = [];
  const emailTo: string[] = [];
  const seenPush = new Set<string>();
  const seenEmail = new Set<string>();
  for (const recipient of recipients) {
    if (subscribedUserIds.has(recipient.userId) && !seenPush.has(recipient.userId)) {
      seenPush.add(recipient.userId);
      pushUserIds.push(recipient.userId);
    }
    const email = recipient.email?.trim().toLowerCase() ?? '';
    if (recipient.emailAllowed && email && !seenEmail.has(email)) {
      seenEmail.add(email);
      emailTo.push(email);
    }
  }
  return { pushUserIds, emailTo };
}
